import InlineStatusField from '../ui/InlineStatusField'
import RecurrenceEditor from '../ui/RecurrenceEditor'
import CardTimePanel from './CardTimePanel'
import { getInitials, getProcedureLabel } from './cardDetailUtils'

export default function CardTicketDetailsPanel({
    t,
    card,
    companyMembers,
    fieldStatus,
    renderFieldState,
    runFieldUpdate,
    isAdmin,
    configLocked,
    epics,
    completionFields,
    readOnly,
    effectiveTimerStartedAt,
    doneForCurrentUser,
    procedures,
    procedureIndexMap,
    timeLogs,
    groupedTimeLogs,
    activeTimer,
    effectiveIsCompleted,
    userId,
    loadingAverage,
    averageMinutes,
    linkedProcedureId,
}) {
    const creator = card?.created_by
        ? (companyMembers || []).find((member) => member.id === card.created_by)
        : null
    const creatorName = creator?.full_name || creator?.email

    return (
        <div className="card-detail-sidebar-section">
            <h4 className="cd-sidebar-heading">{t('card.sidebar.ticketDetails')}</h4>

            {creatorName ? (
                <div className="cd-created-by-row">
                    <span className="cd-field-label">{t('card.sidebar.createdBy', { defaultValue: 'Creat de' })}</span>
                    <div className="cd-created-by">
                        <span className="cd-created-by-avatar">{getInitials(creatorName)}</span>
                        <span className="cd-created-by-name">{creatorName}</span>
                    </div>
                </div>
            ) : null}

            <InlineStatusField
                label={t('card.sidebar.issueType')}
                status={fieldStatus.issue_type?.status || 'idle'}
                errorMessage={fieldStatus.issue_type?.errorMessage || ''}
                className="card-detail-field"
            >
                <label className="cd-field-label">{t('card.sidebar.issueType')}</label>
                <select
                    className="inline-input cd-field-select"
                    value={card.issue_type || 'task'}
                    onChange={(event) => runFieldUpdate('issue_type', { issue_type: event.target.value })}
                    disabled={!isAdmin || configLocked}
                >
                    <option value="task">Task</option>
                    <option value="bug">Bug</option>
                    <option value="story">Story</option>
                    <option value="epic">Epic</option>
                    <option value="sub-task">Sub-task</option>
                </select>
                {renderFieldState('issue_type')}
            </InlineStatusField>

            {card.issue_type === 'sub-task' && (
                <div className="cd-field-group">
                    <label className="cd-field-label">{t('card.sidebar.parentEpic')}</label>
                    <select
                        className="inline-input cd-field-select"
                        value={card.parent_id || ''}
                        onChange={(event) => runFieldUpdate('parent_id', { parent_id: event.target.value || null })}
                        disabled={!isAdmin || configLocked}
                    >
                        <option value="">{t('card.sidebar.noParent')}</option>
                        {epics.map((epic) => (
                            <option key={epic.id} value={epic.id}>{epic.title}</option>
                        ))}
                    </select>
                    {renderFieldState('parent_id')}
                </div>
            )}

            <div className="cd-field-group-lg">
                <label className="cd-field-label">{t('card.sidebar.priorityLabel')}</label>
                <select
                    className="inline-input cd-field-select"
                    value={card.priority || 'medium'}
                    onChange={(event) => runFieldUpdate('priority', { priority: event.target.value })}
                    disabled={!isAdmin || configLocked}
                >
                    <option value="highest">{t('priority.urgent')}</option>
                    <option value="high">{t('priority.high')}</option>
                    <option value="medium">{t('priority.medium')}</option>
                    <option value="low">{t('priority.low')}</option>
                    <option value="lowest">{t('priority.lowest')}</option>
                </select>
                {renderFieldState('priority')}
            </div>

            {(() => {
                const kpis = completionFields || []
                const hasKpi = kpis.length > 0 && kpis.some((field) => Number(field.points) > 0)
                if (hasKpi) {
                    return (
                        <div className="cd-field-group-lg">
                            <label className="cd-field-label">{t('card.sidebar.pointsLabel', { defaultValue: 'Punctaj' })}</label>
                            <div
                                className="inline-input cd-field-input"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    background: 'rgba(34,197,94,0.08)',
                                    border: '1px solid rgba(34,197,94,0.3)',
                                    color: 'var(--accent-green, #16a34a)',
                                    fontWeight: 600,
                                    cursor: 'default',
                                }}
                                title={t('card.sidebar.pointsKpiHint', { defaultValue: 'Calculat din KPI × puncte/unitate la finalizare' })}
                            >
                                ⭐ {kpis.reduce((sum, field) => sum + (Number(field.points) || 0), 0)} pt/unitate × {kpis.length} KPI
                            </div>
                        </div>
                    )
                }
                return (
                    <div className="cd-field-group-lg">
                        <label className="cd-field-label">{t('card.sidebar.pointsLabel', { defaultValue: 'Punctaj' })}</label>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            className="inline-input cd-field-input"
                            value={card.point_value ?? ''}
                            onChange={(event) => {
                                const raw = event.target.value
                                const next = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0))
                                runFieldUpdate('point_value', { point_value: next })
                            }}
                            disabled={readOnly || effectiveTimerStartedAt || doneForCurrentUser}
                            placeholder={t('card.sidebar.pointsPlaceholder', { defaultValue: 'ex: 5' })}
                            style={Number(card.point_value || 0) > 0 ? {
                                background: 'rgba(34,197,94,0.08)',
                                borderColor: 'rgba(34,197,94,0.3)',
                                color: 'var(--accent-green, #16a34a)',
                                fontWeight: 600,
                            } : undefined}
                            title={(effectiveTimerStartedAt || doneForCurrentUser)
                                ? t('card.sidebar.pointsLocked', { defaultValue: 'Punctajul nu mai poate fi modificat după ce ai pornit sau finalizat task-ul.' })
                                : t('card.sidebar.pointsFlatHint', { defaultValue: 'Punctaj fix acordat la finalizare' })}
                        />
                        {renderFieldState('point_value')}
                    </div>
                )
            })()}

            <div className="cd-field-group-lg">
                <label className="cd-field-label">{t('card.sidebar.linkedProcedure')}</label>
                <select
                    className="inline-input cd-field-select"
                    value={linkedProcedureId || card.procedure_id || ''}
                    onChange={(event) => runFieldUpdate('procedure_id', { procedure_id: event.target.value || null })}
                    disabled={!isAdmin || configLocked}
                >
                    <option value="">{t('card.sidebar.noProcedure')}</option>
                    {procedures.map((procedure) => (
                        <option key={procedure.id} value={procedure.id}>
                            {getProcedureLabel(procedure, procedureIndexMap.get(procedure.id))}{procedure.frequency ? ` (${procedure.frequency})` : ''}
                        </option>
                    ))}
                </select>
                {renderFieldState('procedure_id')}
            </div>

            <div className="cd-field-group-lg">
                <label className="cd-field-label">{t('card.sidebar.repeat')}</label>
                <RecurrenceEditor
                    rule={card.recurrence_rule || ''}
                    onChange={(newRule) => runFieldUpdate('recurrence_rule', { recurrence_rule: newRule || null })}
                    disabled={configLocked}
                />
            </div>

            <CardTimePanel
                t={t}
                card={card}
                timeLogs={timeLogs}
                groupedTimeLogs={groupedTimeLogs}
                activeTimer={activeTimer}
                effectiveIsCompleted={effectiveIsCompleted}
                effectiveTimerStartedAt={effectiveTimerStartedAt}
                userId={userId}
                companyMembers={companyMembers}
                loadingAverage={loadingAverage}
                averageMinutes={averageMinutes}
            />
        </div>
    )
}
